import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { updateSubcategory, deleteSubcategory } from '../services/api';

const SubcategoryTable = ({ subcategories, onRefresh, loading }) => {
  const [editingSubcategory, setEditingSubcategory] = useState(null);
  const [editFormData, setEditFormData] = useState({
    name: '',
    categoryId: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleEdit = (subcategory) => {
    setEditingSubcategory(subcategory.id);
    setEditFormData({
      name: subcategory.name,
      categoryId: subcategory.categoryId,
    });
  };

  const handleCancelEdit = () => {
    setEditingSubcategory(null);
    setEditFormData({
      name: '',
      categoryId: '',
    });
  };

  const handleUpdate = async (id) => {
    if (!editFormData.name.trim()) {
      toast.error('Name is required');
      return;
    }

    setIsSubmitting(true);
    try {
      await updateSubcategory(id, editFormData);
      toast.success('Subcategory updated successfully');
      setEditingSubcategory(null);
      onRefresh();
    } catch (error) {
      toast.error('Failed to update subcategory');
      console.error('Error updating subcategory:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this subcategory?')) {
      return;
    }

    setIsSubmitting(true);
    try {
      await deleteSubcategory(id);
      toast.success('Subcategory deleted successfully');
      onRefresh();
    } catch (error) {
      toast.error('Failed to delete subcategory');
      console.error('Error deleting subcategory:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData({
      ...editFormData,
      [name]: value,
    });
  };

  if (loading) {
    return (
      <div className="min-h-[200px] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (subcategories.length === 0) {
    return (
      <div className="bg-white shadow overflow-hidden sm:rounded-lg p-6 text-center text-gray-500">
        No subcategories found.
      </div>
    );
  }

  return (
    <>
      {/* Desktop Table View */}
      <div className="hidden md:block overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                ID
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Name
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Category
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Middle Category
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {subcategories.map((subcategory) => (
              <tr key={subcategory.id}>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {subcategory.id}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {editingSubcategory === subcategory.id ? (
                    <input
                      type="text"
                      name="name"
                      value={editFormData.name}
                      onChange={handleInputChange}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      placeholder="Subcategory name"
                      disabled={isSubmitting}
                    />
                  ) : (
                    subcategory.name
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {subcategory.categoryName}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {subcategory.middleCategoryName || '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  {editingSubcategory === subcategory.id ? (
                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={() => handleUpdate(subcategory.id)}
                        className="text-indigo-600 hover:text-indigo-900"
                        disabled={isSubmitting}
                      >
                        Save
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="text-gray-600 hover:text-gray-900"
                        disabled={isSubmitting}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={() => handleEdit(subcategory)}
                        className="text-indigo-600 hover:text-indigo-900"
                        disabled={isSubmitting}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(subcategory.id)}
                        className="text-red-600 hover:text-red-900"
                        disabled={isSubmitting}
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-4">
        {subcategories.map((subcategory) => (
          <div key={subcategory.id} className="bg-white rounded-lg shadow overflow-hidden">
            <div className="p-4">
              {editingSubcategory === subcategory.id ? (
                <div className="space-y-4">
                  <div>
                    <label htmlFor={`name-${subcategory.id}`} className="block text-sm font-medium text-gray-700">
                      Name
                    </label>
                    <input
                      id={`name-${subcategory.id}`}
                      type="text"
                      name="name"
                      value={editFormData.name}
                      onChange={handleInputChange}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      placeholder="Subcategory name"
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="flex justify-end space-x-2">
                    <button
                      onClick={handleCancelEdit}
                      className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => handleUpdate(subcategory.id)}
                      className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      disabled={isSubmitting}
                    >
                      Save
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex justify-between">
                    <h3 className="text-lg font-medium text-gray-900">{subcategory.name}</h3>
                    <span className="text-sm text-gray-500">ID: {subcategory.id}</span>
                  </div>
                  <div className="mt-2">
                    <span className="text-sm font-medium text-gray-500">Category:</span>
                    <p className="text-sm text-gray-900">{subcategory.categoryName}</p>
                  </div>
                  <div className="mt-2">
                    <span className="text-sm font-medium text-gray-500">Middle Category:</span>
                    <p className="text-sm text-gray-900">{subcategory.middleCategoryName || '-'}</p>
                  </div>
                  <div className="mt-4 flex justify-end space-x-2">
                    <button
                      onClick={() => handleEdit(subcategory)}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      disabled={isSubmitting}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(subcategory.id)}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                      disabled={isSubmitting}
                    >
                      Delete
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export default SubcategoryTable; 